import * as React from "react";
import { Loader2, Inbox, Server, Globe, ArrowLeft, Save, Eye, EyeOff, Zap } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAdminProvisioning, useUpdateProvisioning, useProvisionHosting, useProvisionDomain } from "@/hooks/useAdmin";
import { formatDate } from "@/lib/utils";
import type { AdminProvisioningDto } from "@/types/api";

const FILTERS = [
  { key: "Pending", label: "To do" },
  { key: "InProgress", label: "In progress" },
  { key: "Completed", label: "Done" },
  { key: "", label: "All" }
] as const;

const STEPS: { key: "domain_configured" | "hosting_configured" | "email_configured" | "ssl_configured"; label: string }[] = [
  { key: "domain_configured", label: "Domain" },
  { key: "hosting_configured", label: "Hosting" },
  { key: "email_configured", label: "Email" },
  { key: "ssl_configured", label: "SSL" }
];

function doneCount(p: AdminProvisioningDto) {
  return [p.domain_configured, p.hosting_configured, p.email_configured, p.ssl_configured].filter(Boolean).length;
}

export function AdminProvisioningPage() {
  const [status, setStatus] = React.useState<string>("Pending");
  const [q, setQ] = React.useState("");
  const [selectedId, setSelectedId] = React.useState<number | undefined>();
  const { data: list, isLoading } = useAdminProvisioning({ status, q });

  const selected = list?.find(p => p.id === selectedId);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">Setup queue</h2>
        <p className="text-sm text-muted-foreground">
          Every purchased domain to configure — domain, hosting, email, SSL. Tick steps as you go.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setStatus(f.key)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
              status === f.key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"
            }`}
          >
            {f.label}
          </button>
        ))}
        <Input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search domain or customer…"
          className="ml-auto w-full max-w-xs"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        {/* List */}
        <div className={`${selected ? "hidden lg:block" : ""} space-y-2`}>
          {isLoading ? (
            <div className="flex h-32 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (list?.length ?? 0) === 0 ? (
            <Card><CardContent className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
              <Inbox className="h-8 w-8" /><p>Nothing here.</p>
            </CardContent></Card>
          ) : (
            list!.map(p => (
              <button
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                className={`w-full rounded-lg border p-3 text-left transition ${
                  selectedId === p.id ? "border-primary bg-brand-accent/5" : "border-border hover:border-primary/40"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-2 font-medium">
                    <Globe className="h-3.5 w-3.5 text-muted-foreground" /> {p.domain}
                  </span>
                  <StatusBadge status={p.status} />
                </div>
                <p className="mt-1 truncate text-sm text-muted-foreground">
                  {p.customer_name || p.customer_email} · {p.plan_name || "—"} · <span className="capitalize">{p.mode.toLowerCase()}</span>
                </p>
                <div className="mt-2 flex items-center justify-between">
                  <Progress p={p} />
                  <span className="text-[11px] text-muted-foreground">{formatDate(p.created_at)}</span>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Detail */}
        <div className={selected ? "" : "hidden lg:block"}>
          {selected ? (
            <DetailPanel key={selected.id} item={selected} onBack={() => setSelectedId(undefined)} />
          ) : (
            <Card><CardContent className="flex h-full min-h-[300px] items-center justify-center text-sm text-muted-foreground">
              Select a domain to configure it.
            </CardContent></Card>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailPanel({ item, onBack }: { item: AdminProvisioningDto; onBack: () => void }) {
  const update = useUpdateProvisioning();
  const provision = useProvisionHosting();
  const provisionDomain = useProvisionDomain();
  const { toast } = useToast();
  const [retryEpp, setRetryEpp] = React.useState("");
  const [steps, setSteps] = React.useState({
    domain_configured: item.domain_configured,
    hosting_configured: item.hosting_configured,
    email_configured: item.email_configured,
    ssl_configured: item.ssl_configured
  });
  const [packageId, setPackageId] = React.useState(item.twentyi_package_id ?? "");
  const [note, setNote] = React.useState(item.note ?? "");
  const [showSensitive, setShowSensitive] = React.useState(false);

  async function save() {
    try {
      await update.mutateAsync({
        id: item.id,
        body: {
          ...steps,
          twentyi_package_id: packageId.trim() || undefined,
          note
        }
      });
      toast({ title: "Saved", variant: "success" });
    } catch {
      toast({ title: "Couldn't save", variant: "destructive" });
    }
  }

  async function provisionNow() {
    try {
      const res = await provision.mutateAsync(item.id);
      if (res.success) {
        toast({ title: "Provisioned", description: `20i package ${res.package_id} created.`, variant: "success" });
        if (res.package_id) setPackageId(res.package_id);
      } else {
        toast({ title: "Provisioning failed", description: res.message, variant: "destructive" });
      }
    } catch {
      toast({ title: "Provisioning failed", description: "Couldn't reach 20i. Try again.", variant: "destructive" });
    }
  }

  return (
    <Card className="flex flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={onBack} aria-label="Back"><ArrowLeft className="h-4 w-4" /></Button>
          <span className="inline-flex items-center gap-2 font-semibold"><Globe className="h-4 w-4" /> {item.domain}</span>
        </div>
        <StatusBadge status={item.status} />
      </div>

      <CardContent className="space-y-5 py-4">
        {/* Who / what */}
        <div className="space-y-1 text-sm">
          <Row label="Customer" value={`${item.customer_name || "—"} · ${item.customer_email || ""}`} />
          <Row label="Plan" value={item.plan_name || "—"} />
          <Row label="Acquisition" value={<span className="capitalize">{item.mode.toLowerCase()}</span>} />
          <Row label="Allowances" value={`${alw(item.mailboxes)} mailboxes · ${alw(item.websites)} sites · ${item.storage_gb} GB`} />
        </div>

        {/* Captured data */}
        {(item.registrant_json || item.epp_code || item.nameservers_json) && (
          <div className="rounded-lg border border-border p-3">
            <button onClick={() => setShowSensitive(v => !v)} className="mb-2 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
              {showSensitive ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              {showSensitive ? "Hide" : "Show"} captured data
            </button>
            {showSensitive && (
              <div className="space-y-1 text-xs">
                {item.epp_code && <p><span className="text-muted-foreground">EPP:</span> <code>{item.epp_code}</code></p>}
                {item.nameservers_json && <p><span className="text-muted-foreground">Nameservers:</span> <code className="break-all">{item.nameservers_json}</code></p>}
                {item.registrant_json && <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded bg-muted p-2">{item.registrant_json}</pre>}
              </div>
            )}
          </div>
        )}

        {/* Checklist */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Setup steps</p>
          <div className="grid grid-cols-2 gap-2">
            {STEPS.map(s => (
              <label key={s.key} className={`flex cursor-pointer items-center gap-2 rounded-lg border p-2.5 text-sm ${
                steps[s.key] ? "border-brand-green/40 bg-brand-green/5" : "border-border"
              }`}>
                <input
                  type="checkbox"
                  checked={steps[s.key]}
                  onChange={e => setSteps(prev => ({ ...prev, [s.key]: e.target.checked }))}
                  className="h-4 w-4 accent-[hsl(var(--primary))]"
                />
                {s.label}
              </label>
            ))}
          </div>
        </div>

        {/* 20i package id */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">20i package ID</label>
          <div className="flex gap-2">
            <Input value={packageId} onChange={e => setPackageId(e.target.value)} placeholder="e.g. 1234567" className="flex-1" />
            <Button type="button" variant="outline" onClick={provisionNow} disabled={provision.isPending} title="Create the 20i package now via the API">
              {provision.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
              Provision via 20i
            </Button>
          </div>
          <p className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Server className="h-3 w-3" /> "Provision via 20i" creates the package (addWeb) and stamps this automatically. Or paste an existing id. Either way activates the customer's dashboard.
          </p>
        </div>

        {/* Domain register/transfer retry (with corrected EPP) */}
        {!item.domain_configured && (
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Domain {item.mode.toLowerCase() === "transfer" ? "transfer" : "registration"} retry
            </label>
            <div className="flex gap-2">
              {item.mode.toLowerCase() === "transfer" && (
                <Input
                  value={retryEpp}
                  onChange={e => setRetryEpp(e.target.value)}
                  placeholder="New EPP / auth code (optional — reuses stored code if blank)"
                  className="flex-1"
                />
              )}
              <Button
                type="button"
                variant="outline"
                disabled={provisionDomain.isPending}
                title="Re-submit the domain register/transfer to the registrar"
                onClick={async () => {
                  try {
                    const res = await provisionDomain.mutateAsync({
                      id: item.id,
                      eppCode: retryEpp.trim() || undefined
                    });
                    if (res.success) {
                      toast({ title: "Submitted", description: res.message, variant: "success" });
                      setRetryEpp("");
                    } else {
                      toast({ title: "Retry failed", description: res.message, variant: "destructive" });
                    }
                  } catch (err) {
                    toast({
                      title: "Retry failed",
                      description: err instanceof Error ? err.message : "Registrar error.",
                      variant: "destructive"
                    });
                  }
                }}
              >
                {provisionDomain.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
                Retry domain
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Use after a failed transfer (bad EPP, locked domain) — the customer already paid, so the
              retry is free. For transfers, paste the corrected EPP first.
            </p>
          </div>
        )}

        {/* Note */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Note</label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
            placeholder="Internal note (optional)…"
            className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        <Button className="w-full" onClick={save} disabled={update.isPending}>
          {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </Button>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

function Progress({ p }: { p: AdminProvisioningDto }) {
  const n = doneCount(p);
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
      <span className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
        <span className="block h-full bg-brand-green" style={{ width: `${(n / 4) * 100}%` }} />
      </span>
      {n}/4
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === "Completed") return <Badge variant="success">Done</Badge>;
  if (status === "Failed") return <Badge variant="destructive">Failed</Badge>;
  if (status === "InProgress") return <Badge variant="outline" className="text-amber-600">In progress</Badge>;
  return <Badge variant="outline">To do</Badge>;
}

function alw(n: number) {
  return n < 0 ? "∞" : n;
}
